import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import LinkIcon from '@mui/icons-material/Link'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { sendSupportPaymentLink } from 'src/supportPortal/services/paymentLinksService'

function ChannelIcon({ channel }) {
  if (channel === 'mail') {
    return <MailOutlineIcon sx={{ fontSize: 18 }} />
  }
  if (channel === 'whatsapp') {
    return <WhatsAppIcon sx={{ fontSize: 18 }} />
  }
  return <LinkIcon sx={{ fontSize: 18 }} />
}

export default function SupportPaymentLinkSendDialog({ open, onClose, row, onSent }) {
  const [customEmail, setCustomEmail] = useState('')
  const [customWhatsapp, setCustomWhatsapp] = useState('')
  const [sendingPayload, setSendingPayload] = useState(null)

  useEffect(() => {
    if (!open) {
      setCustomEmail('')
      setCustomWhatsapp('')
      setSendingPayload(null)
    }
  }, [open])

  const canSend = row?.canSend === true

  const handleSend = async (payload) => {
    if (!row?.id || !payload?.channel) {
      return
    }
    setSendingPayload(payload)
    const result = await sendSupportPaymentLink({
      paymentLinkId: row.id,
      ...payload,
    })
    setSendingPayload(null)
    if (result?.ok) {
      toast.success('Payment link sent')
      onSent?.()
      return
    }
    toast.error(result?.message || 'Could not send payment link')
  }

  const existingContacts = Array.isArray(row?.contactOptions) ? row.contactOptions : []
  const nonHumanContacts = existingContacts.filter((item) => item?.destinationType !== 'human-support')
  const humanSupportContact = existingContacts.find((item) => item?.destinationType === 'human-support')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Send Payment Link
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row?.itemTitle || 'Payment link'}
          </Typography>
          {row?.paymentLink ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {row.paymentLink}
            </Typography>
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canSend ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {row?.accessSummary || 'This role is view-only for this payment link.'}
          </Alert>
        ) : null}

        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Patient Contacts
            </Typography>
            <Stack spacing={1}>
              {nonHumanContacts.length > 0 ? (
                nonHumanContacts.map((contact) => (
                  <Paper
                    key={contact?.id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {contact?.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contact?.displayValue}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChannelIcon channel={contact?.channel} />}
                      disabled={!canSend || sendingPayload?.id === contact?.id}
                      onClick={() =>
                        handleSend({
                          id: contact?.id,
                          channel: contact?.channel,
                          destinationType: contact?.destinationType,
                          value: contact?.value,
                        })
                      }
                    >
                      {sendingPayload?.id === contact?.id
                        ? 'Sending...'
                        : `Send via ${contact?.channel === 'mail' ? 'Email' : 'WhatsApp'}`}
                    </Button>
                  </Paper>
                ))
              ) : (
                <Alert severity="info">No patient contacts were resolved for this payment link.</Alert>
              )}
            </Stack>
          </Box>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Custom Email
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="name@example.com"
                  value={customEmail}
                  onChange={(event) => setCustomEmail(event.target.value)}
                />
                <Button
                  variant="outlined"
                  startIcon={<MailOutlineIcon />}
                  disabled={!canSend || !customEmail.trim() || sendingPayload?.id === 'custom-email'}
                  onClick={() =>
                    handleSend({
                      id: 'custom-email',
                      channel: 'mail',
                      destinationType: 'custom-email',
                      value: customEmail.trim(),
                    })
                  }
                >
                  {sendingPayload?.id === 'custom-email' ? 'Sending...' : 'Send Email'}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Custom WhatsApp
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="+91..."
                  value={customWhatsapp}
                  onChange={(event) => setCustomWhatsapp(event.target.value)}
                />
                <Button
                  variant="outlined"
                  startIcon={<WhatsAppIcon />}
                  disabled={!canSend || !customWhatsapp.trim() || sendingPayload?.id === 'custom-whatsapp'}
                  onClick={() =>
                    handleSend({
                      id: 'custom-whatsapp',
                      channel: 'whatsapp',
                      destinationType: 'custom-whatsapp',
                      value: customWhatsapp.trim(),
                    })
                  }
                >
                  {sendingPayload?.id === 'custom-whatsapp' ? 'Sending...' : 'Send WhatsApp'}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Support Routing
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SupportAgentIcon />}
              disabled={!canSend || !humanSupportContact?.value || sendingPayload?.id === 'human-support'}
              onClick={() =>
                handleSend({
                  id: 'human-support',
                  channel: 'whatsapp',
                  destinationType: 'human-support',
                  value: humanSupportContact?.value,
                })
              }
            >
              {sendingPayload?.id === 'human-support' ? 'Sending...' : 'Send To Human Support'}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
